import React from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { Button } from "../../../components/ui/button";
import { FieldLabel, TextInput } from "../../../components/ui/FormControls";
import { SurfaceCard } from "../../../components/ui/SurfaceCard";
import { ssrFormattedDateOr } from "../../../lib/ssrFormattedDate";
import type { IncomingOrgInvitation, MainAppPageProps } from "../types";

type IncomingOrgInvitationsCardProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes"
> & {
  invitations: IncomingOrgInvitation[];
  title?: string;
  needsDisplayName?: boolean;
  variant?: "default" | "highlight";
};

function invitationAction(
  template: string | undefined,
  id: string,
  fallback: string,
) {
  return (template ?? fallback).replace(":id", id);
}

function inviteHeadline(invite: IncomingOrgInvitation): string {
  return `${invite.organizationName} is inviting you`;
}

function roleArticle(role: string): string {
  return /^[aeiou]/i.test(role) ? "an" : "a";
}

function inviteDetails(invite: IncomingOrgInvitation): string {
  const asRole = `${roleArticle(invite.role)} ${invite.role}`;
  if (invite.invitedByName) {
    return `${invite.invitedByName} invited you as ${asRole}.`;
  }
  return `You're invited as ${asRole}.`;
}

function DisplayNameField({
  needsDisplayName,
  idSuffix,
}: {
  needsDisplayName: boolean;
  idSuffix: string;
}) {
  if (!needsDisplayName) return null;

  return (
    <div className="mb-3">
      <FieldLabel htmlFor={`display_name_${idSuffix}`}>
        Your display name
      </FieldLabel>
      <TextInput
        id={`display_name_${idSuffix}`}
        name="display_name"
        placeholder="Name your team will see"
        required
        maxLength={100}
      />
    </div>
  );
}

function InvitationActions({
  csrfToken,
  routes,
  invite,
  needsDisplayName,
  stacked = false,
}: Pick<IncomingOrgInvitationsCardProps, "csrfToken" | "routes"> & {
  invite: IncomingOrgInvitation;
  needsDisplayName: boolean;
  stacked?: boolean;
}) {
  const joinLabel = invite.currentOrganizationName ? "Leave and join" : "Join";

  return (
    <div className={stacked ? "flex flex-col gap-2" : "flex flex-wrap gap-2"}>
      <form
        action={invitationAction(
          routes.acceptIncomingOrganizationInvitation,
          invite.id,
          `/organization/incoming-invitations/${invite.id}/accept`,
        )}
        method="post"
        className={
          stacked
            ? "flex w-full flex-col gap-2"
            : "flex min-w-[12rem] flex-1 flex-col gap-2"
        }
      >
        <AuthenticityTokenField csrfToken={csrfToken} />
        <DisplayNameField
          needsDisplayName={needsDisplayName}
          idSuffix={invite.id}
        />
        <Button
          type="submit"
          variant="premium"
          size={stacked ? "default" : "sm"}
          className={stacked ? "w-full" : undefined}
        >
          {joinLabel}
        </Button>
      </form>
      <form
        action={invitationAction(
          routes.declineIncomingOrganizationInvitation,
          invite.id,
          `/organization/incoming-invitations/${invite.id}/decline`,
        )}
        method="post"
        className={stacked ? "w-full" : undefined}
      >
        <AuthenticityTokenField csrfToken={csrfToken} />
        <Button
          type="submit"
          variant="outline"
          size={stacked ? "default" : "sm"}
          className={stacked ? "w-full" : undefined}
        >
          Decline
        </Button>
      </form>
    </div>
  );
}

function InvitationMeta({ invite }: { invite: IncomingOrgInvitation }) {
  return (
    <>
      {invite.currentOrganizationName ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Joining leaves {invite.currentOrganizationName}.
        </p>
      ) : null}
      {invite.expiresAtDisplay ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Expires {ssrFormattedDateOr(invite.expiresAtDisplay)}
        </p>
      ) : null}
    </>
  );
}

export const IncomingOrgInvitationsCard: React.FC<
  IncomingOrgInvitationsCardProps
> = ({
  csrfToken,
  routes,
  invitations,
  title = "Invites for you",
  needsDisplayName = false,
  variant = "default",
}) => {
  if (invitations.length === 0) return null;

  if (variant === "highlight") {
    return (
      <div className="mb-8 flex flex-col gap-4">
        {invitations.map((invite) => (
          <SurfaceCard
            key={invite.id}
            className="flex flex-col gap-4 rounded-2xl border-primary bg-primary/15 p-8 shadow-lg"
          >
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {inviteHeadline(invite)}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {inviteDetails(invite)}
              </p>
              <InvitationMeta invite={invite} />
            </div>
            <InvitationActions
              csrfToken={csrfToken}
              routes={routes}
              invite={invite}
              needsDisplayName={needsDisplayName}
              stacked
            />
          </SurfaceCard>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
        {invitations.map((invite) => (
          <li key={invite.id} className="flex flex-col gap-3 p-4">
            <div>
              <p className="text-sm font-medium text-foreground">
                {invite.organizationName}
              </p>
              <p className="text-sm text-muted-foreground">
                <span className="capitalize">{invite.role}</span>
                {invite.invitedByName ? ` · from ${invite.invitedByName}` : ""}
              </p>
              <InvitationMeta invite={invite} />
            </div>
            <InvitationActions
              csrfToken={csrfToken}
              routes={routes}
              invite={invite}
              needsDisplayName={needsDisplayName}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};
